// ============================================================================
// ROLE PERMISSION MODAL - Digital Approval System
// ============================================================================
// Create / edit / view modal for admin.tbl_role_permission rows.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ALL_MODULES, MODULE_SECTIONS, SUPER_ROLE_CODE } from 'config/rolePermissions';
import type {
  RolePermission,
  CreateRolePermissionRequest,
  UpdateRolePermissionRequest,
} from 'api/types/admin/rolePermissions.types';

export type RolePermissionModalMode = 'create' | 'edit' | 'view';

interface RolePermissionModalProps {
  mode: RolePermissionModalMode;
  initial: RolePermission | null;
  onClose: () => void;
  onSubmit: (
    payload: CreateRolePermissionRequest | UpdateRolePermissionRequest
  ) => Promise<void>;
}

// ============================================================================
// STYLES
// ============================================================================

const backdropStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)',
  zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '1rem',
};

const containerStyle: React.CSSProperties = {
  background: '#ffffff', borderRadius: '0.625rem',
  width: '100%', maxWidth: '720px', maxHeight: '90vh',
  zIndex: 1001, display: 'flex', flexDirection: 'column',
  boxShadow: '0 24px 48px rgba(15, 23, 42, 0.24)',
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  padding: '0.875rem 1.25rem',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  flexShrink: 0,
};

const bodyStyle: React.CSSProperties = {
  flex: 1, overflowY: 'auto', minHeight: 0,
  padding: '1rem 1.25rem',
  display: 'flex', flexDirection: 'column', gap: '1rem',
};

const footerStyle: React.CSSProperties = {
  padding: '0.75rem 1.25rem',
  borderTop: '1px solid #e5e7eb',
  display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
  flexShrink: 0,
  background: '#f9fafb',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.78rem', fontWeight: 600, color: '#374151',
  display: 'block', marginBottom: '0.25rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%', height: '36px', padding: '0 0.625rem',
  fontSize: '0.85rem', border: '1px solid #d1d5db', borderRadius: '0.375rem',
};

const textareaStyle: React.CSSProperties = {
  width: '100%', minHeight: '70px', padding: '0.5rem 0.625rem',
  fontSize: '0.85rem', border: '1px solid #d1d5db', borderRadius: '0.375rem',
  resize: 'vertical', fontFamily: 'inherit',
};

const btnPrimary: React.CSSProperties = {
  height: '36px', padding: '0 1rem',
  background: '#2563eb', color: '#ffffff', border: 'none',
  borderRadius: '0.375rem', fontSize: '0.85rem', fontWeight: 600,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  ...btnPrimary,
  background: '#f3f4f6', color: '#374151',
};

const moduleCardStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb', borderRadius: '0.375rem',
  padding: '0.625rem 0.75rem',
  display: 'flex', flexDirection: 'column', gap: '0.4rem',
};

// ============================================================================
// MODAL
// ============================================================================

const RolePermissionModal: React.FC<RolePermissionModalProps> = ({
  mode, initial, onClose, onSubmit,
}) => {
  // The super-role (das_system) is fully immutable. If anyone reaches this
  // modal with an Edit mode for das_system (shouldn't happen — the screen
  // hides Edit for that row, but defense-in-depth), force read-only.
  const isSuperRow = initial?.roleCode === SUPER_ROLE_CODE;
  const effectiveMode: RolePermissionModalMode = isSuperRow ? 'view' : mode;
  const readOnly = effectiveMode === 'view';
  const isEdit = effectiveMode === 'edit';

  // For das_system the stored row is irrelevant — the runtime permission
  // is always the full ALL_MODULES catalog. Display that instead of the
  // potentially-stale snapshot in admin.tbl_role_permission.
  const superRoleSnapshot = useMemo(() => {
    if (!isSuperRow) return null;
    const visibleModules = new Set<string>();
    const moduleActions: Record<string, Set<string>> = {};
    for (const m of ALL_MODULES) {
      visibleModules.add(m.id);
      moduleActions[m.id] = new Set(m.actions);
    }
    return { visibleModules, moduleActions };
  }, [isSuperRow]);

  const [roleCode, setRoleCode] = useState(initial?.roleCode ?? '');
  const [roleName, setRoleName] = useState(initial?.roleName ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  // Track selected modules + per-module action sets as Sets for easy toggling.
  // For das_system we seed from the catalog snapshot so the checkboxes show
  // a complete tick across every module + action.
  const [visibleModules, setVisibleModules] = useState<Set<string>>(
    () => superRoleSnapshot?.visibleModules ?? new Set(initial?.visibleModules ?? [])
  );
  const [moduleActions, setModuleActions] = useState<Record<string, Set<string>>>(
    () => superRoleSnapshot?.moduleActions ?? Object.fromEntries(
      Object.entries(initial?.moduleActions ?? {}).map(([k, v]) => [k, new Set(v ?? [])])
    )
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close on ESC.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleModule = useCallback((moduleId: string) => {
    setVisibleModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
        // Drop its actions too.
        setModuleActions((ma) => {
          const { [moduleId]: _, ...rest } = ma;
          return rest;
        });
      } else {
        next.add(moduleId);
      }
      return next;
    });
  }, []);

  const toggleAction = useCallback((moduleId: string, action: string) => {
    setModuleActions((prev) => {
      const next = { ...prev };
      const cur = new Set(next[moduleId] ?? []);
      if (cur.has(action)) cur.delete(action); else cur.add(action);
      next[moduleId] = cur;
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    setError(null);

    if (!roleName.trim()) {
      setError('Role name is required.');
      return;
    }
    if (mode === 'create' && !roleCode.trim()) {
      setError('Role code is required.');
      return;
    }

    const moduleActionsObj: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(moduleActions)) {
      // Skip modules that aren't selected as visible.
      if (!visibleModules.has(k)) continue;
      moduleActionsObj[k] = [...v];
    }

    setSaving(true);
    try {
      if (mode === 'create') {
        const payload: CreateRolePermissionRequest = {
          roleCode: roleCode.trim(),
          roleName: roleName.trim(),
          description: description.trim() || null,
          visibleModules: [...visibleModules],
          moduleActions: moduleActionsObj,
          isSystemRole: false,
          isActive,
        };
        await onSubmit(payload);
      } else if (mode === 'edit' && initial) {
        const payload: UpdateRolePermissionRequest = {
          roleName: roleName.trim(),
          description: description.trim() || null,
          visibleModules: [...visibleModules],
          moduleActions: moduleActionsObj,
          isActive,
          rowVersion: initial.rowVersion,
        };
        await onSubmit(payload);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [mode, initial, roleCode, roleName, description, isActive, visibleModules, moduleActions, onSubmit]);

  const title = useMemo(() => {
    if (effectiveMode === 'create') return 'Add Role';
    if (effectiveMode === 'edit') return `Edit Role: ${initial?.roleName ?? ''}`;
    return `View Role: ${initial?.roleName ?? ''}`;
  }, [effectiveMode, initial]);

  const content = (
    <div style={backdropStyle} role="presentation">
      <div
        style={containerStyle}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={headerStyle}>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1f2937' }}>
            {title}
            {initial?.isSystemRole && (
              <span style={{
                marginLeft: '0.5rem',
                fontSize: '0.7rem',
                padding: '0.05rem 0.4rem',
                borderRadius: '0.25rem',
                background: '#eef2ff',
                color: '#4338ca',
                fontWeight: 600,
                verticalAlign: 'middle',
              }}>
                SYSTEM
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', fontSize: '1.25rem',
              cursor: 'pointer', color: '#6b7280', padding: '0.25rem 0.5rem',
            }}
          >
            ×
          </button>
        </div>

        <div style={bodyStyle}>
          {error && (
            <div style={{
              padding: '0.625rem 0.875rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '0.375rem',
              color: '#dc2626',
              fontSize: '0.85rem',
            }}>
              {error}
            </div>
          )}

          {isSuperRow && (
            <div style={{
              padding: '0.625rem 0.875rem',
              background: '#eef2ff',
              border: '1px solid #c7d2fe',
              borderRadius: '0.375rem',
              color: '#3730a3',
              fontSize: '0.82rem',
              lineHeight: 1.45,
            }}>
              <strong>System role — managed automatically.</strong>{' '}
              das_system always has full access to every module and action defined in the application,
              including any screens added in the future. This role cannot be modified.
            </div>
          )}

          {/* Basic info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Role Code {mode === 'create' && <span style={{ color: '#dc2626' }}>*</span>}</label>
              <input
                style={{ ...inputStyle, background: (isEdit || readOnly) ? '#f3f4f6' : '#fff' }}
                value={roleCode}
                onChange={(e) => setRoleCode(e.target.value)}
                disabled={isEdit || readOnly}
                placeholder="e.g. das_admin"
                maxLength={50}
              />
            </div>
            <div>
              <label style={labelStyle}>Role Name <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                style={inputStyle}
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                disabled={readOnly}
                placeholder="e.g. Administrator"
                maxLength={100}
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              style={textareaStyle}
              value={description ?? ''}
              onChange={(e) => setDescription(e.target.value)}
              disabled={readOnly}
              placeholder="What this role can do"
              maxLength={500}
            />
          </div>

          <div>
            <label style={{ ...labelStyle, marginBottom: '0.5rem' }}>
              Visible Modules &amp; Actions
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {MODULE_SECTIONS.map((sectionName) => {
                const sectionModules = ALL_MODULES.filter((m) => m.section === sectionName);
                if (sectionModules.length === 0) return null;
                return (
                  <div key={sectionName} style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.4rem',
                      paddingBottom: '0.25rem',
                      borderBottom: '1px solid #e5e7eb',
                    }}>
                      <span style={{
                        fontSize: '0.72rem', fontWeight: 700,
                        color: '#6b7280', textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>
                        {sectionName}
                      </span>
                      <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                        ({sectionModules.length})
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {sectionModules.map((m) => {
                        const checked = visibleModules.has(m.id);
                        const actions = moduleActions[m.id] ?? new Set<string>();
                        return (
                          <div key={m.id} style={moduleCardStyle}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: readOnly ? 'default' : 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleModule(m.id)}
                                disabled={readOnly}
                              />
                              <span style={{ fontWeight: 600, color: '#1f2937', fontSize: '0.88rem' }}>{m.label}</span>
                              <code style={{ color: '#9ca3af', fontSize: '0.72rem' }}>{m.id}</code>
                            </label>
                            {checked && m.actions.length > 0 && (
                              <div style={{
                                display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.75rem',
                                paddingLeft: '1.5rem',
                              }}>
                                {m.actions.map((a) => (
                                  <label
                                    key={a}
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                                      fontSize: '0.78rem', color: '#374151',
                                      cursor: readOnly ? 'default' : 'pointer',
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={actions.has(a)}
                                      onChange={() => toggleAction(m.id, a)}
                                      disabled={readOnly}
                                    />
                                    {a}
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={readOnly}
              />
              Active
            </label>
          </div>
        </div>

        <div style={footerStyle}>
          <button style={btnSecondary} onClick={onClose} disabled={saving}>
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button style={btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default RolePermissionModal;
